/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag
 */
import React from 'react';

import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import { aiProactiveMessages } from '@atlaskit/editor-common/ai-messages';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import Loading from '@atlassian/generative-ai-modal/assets/Loading';

import type { AIProactivePluginConfig } from '../../../aiProactivePluginType';
import type { Recommendation } from '../../../pm-plugins/ai-proactive/api';

// Remove below line while cleaning platform_editor_ai_object_sidebar_injection
import { HeadingItem } from './heading-item';
import { RecommendationItem, SpellingGrammarItem } from './recommendation-item';
import { EnableSuggestionsState } from './states/enable-suggestions';
import { NoSuggestionsState } from './states/no-suggestions';
import { ReloadSuggestionsState } from './states/reload-suggestions';

const styles = cssMap({
	loading: {
		paddingBlock: token('space.200'),
		color: token('color.text.subtlest'),
	},

	panelContainer: {
		paddingInlineStart: token('space.0'),
		height: '100%',
		width: '100%',
	},

	emptyState: {
		flex: 4,
	},

	padEmptyState: {
		flex: 1,
	},
});

type RecommendationPanelProps = {
	recommendations: Recommendation[];
	isEditorDisabled?: boolean;
	isLoading: boolean;
	hoveredRecommendationId: string | undefined;
	selectedRecommendationId: string | undefined;
	isSpellingGrammarEnabled: boolean;
	spellingGrammarErrorCount?: number;
	onRecommendationApply: ({ recommendation }: { recommendation: Recommendation }) => void;
	onRecommendationDismiss: ({ recommendation }: { recommendation: Recommendation }) => void;
	onRecommendationSelect: ({ recommendation }: { recommendation: Recommendation }) => void;
	onRecommendationHover: ({ recommendation }: { recommendation: Recommendation }) => void;
	onRecommendationLeave: ({ recommendation }: { recommendation: Recommendation }) => void;
	onSpellingGrammar: () => void;
	onReloadSuggestions: () => void;
	onToggleSuggestions: () => void;
	onAskAI: () => void;
	displayAllSuggestions: boolean;
	isEmptyDocument: boolean;
	hasNoMoreSuggestions: boolean;
	// Remove below 4 props while cleaning platform_editor_ai_object_sidebar_injection
	onClearSelectRecommendation: () => void;
	onPanelClose: () => void;
	config: AIProactivePluginConfig;
	editorView: EditorView;
};

type EmptyStateProps = {
	isLoading: boolean;
	isEditorDisabled?: boolean;
	displayAllSuggestions: boolean;
	onReloadSuggestions: () => void;
	onToggleSuggestions: () => void;
	onAskAI: () => void;
	isEmptyDocument: boolean;
	hasNoMoreSuggestions: boolean;
};

const EmptyState = ({
	isLoading,
	isEditorDisabled,
	displayAllSuggestions,
	onReloadSuggestions,
	isEmptyDocument,
	onToggleSuggestions,
	onAskAI,
	hasNoMoreSuggestions,
}: EmptyStateProps) => {
	const { formatMessage } = useIntl();
	if (!displayAllSuggestions) {
		return <EnableSuggestionsState handleEnable={onToggleSuggestions} />;
	}

	if (isLoading) {
		return (
			<Inline space="space.100" alignInline="center" grow="fill" xcss={styles.loading}>
				<Loading alt="" />
				{formatMessage(aiProactiveMessages.findingSuggestionsLoadingLabel)}
			</Inline>
		);
	}

	if (isEmptyDocument || hasNoMoreSuggestions) {
		return <NoSuggestionsState handleAskAI={onAskAI} isEditorDisabled={isEditorDisabled} />;
	}

	return (
		<ReloadSuggestionsState
			handleReloadSuggestions={onReloadSuggestions}
			isEditorDisabled={isEditorDisabled}
		/>
	);
};

export const RecommendationPanel = (props: RecommendationPanelProps) => {
	const {
		recommendations,
		isEditorDisabled,
		isLoading,
		hoveredRecommendationId,
		selectedRecommendationId,
		isSpellingGrammarEnabled,
		spellingGrammarErrorCount,
		onRecommendationApply,
		onRecommendationDismiss,
		onRecommendationSelect,
		onSpellingGrammar,
		onRecommendationHover,
		onRecommendationLeave,
		onReloadSuggestions,
		onToggleSuggestions,
		onAskAI,
		displayAllSuggestions,
		isEmptyDocument,
		hasNoMoreSuggestions,
		// Remove below 4 props while cleaning platform_editor_ai_object_sidebar_injection
		onPanelClose,
		onClearSelectRecommendation,
		config,
		editorView,
	} = props;

	const { formatMessage } = useIntl();

	return (
		<Stack xcss={styles.panelContainer} space="space.200">
			{!fg('platform_editor_ai_object_sidebar_injection') && (
				<HeadingItem
					onClose={onPanelClose}
					config={config}
					editorView={editorView}
					onClearSelectRecommendation={onClearSelectRecommendation}
				/>
			)}

			<Stack grow="fill" alignBlock="start">
				{recommendations?.length > 0 || (spellingGrammarErrorCount ?? 0) > 0 ? (
					<>
						{isSpellingGrammarEnabled && (spellingGrammarErrorCount ?? 0) > 0 && (
							<SpellingGrammarItem
								errorCount={spellingGrammarErrorCount ?? 0}
								onClick={onSpellingGrammar}
							/>
						)}
						{recommendations?.map((r) => (
							<RecommendationItem
								key={r.id}
								id={r.id}
								action={r.transformAction}
								type={r.type}
								details={r.details}
								isViewed={r.isViewed}
								isHovered={r.id === hoveredRecommendationId}
								isSelected={r.id === selectedRecommendationId}
								isDisabled={isEditorDisabled}
								onApply={() => onRecommendationApply({ recommendation: r })}
								onDismiss={() => onRecommendationDismiss({ recommendation: r })}
								onClick={() => onRecommendationSelect({ recommendation: r })}
								onMouseEnter={() => onRecommendationHover({ recommendation: r })}
								onMouseLeave={() => onRecommendationLeave({ recommendation: r })}
								onPreview={() => onRecommendationSelect({ recommendation: r })}
							/>
						))}
						{isLoading && (
							<Inline space="space.100" grow="fill" xcss={styles.loading}>
								<Loading alt="" />
								{formatMessage(aiProactiveMessages.findingMoreSuggestionsLoadingLabel)}
							</Inline>
						)}
					</>
				) : (
					<>
						<Inline xcss={styles.emptyState} alignBlock="center">
							<EmptyState
								isEditorDisabled={isEditorDisabled}
								isLoading={isLoading}
								displayAllSuggestions={displayAllSuggestions}
								onReloadSuggestions={onReloadSuggestions}
								onToggleSuggestions={onToggleSuggestions}
								onAskAI={onAskAI}
								isEmptyDocument={isEmptyDocument}
								hasNoMoreSuggestions={hasNoMoreSuggestions}
							/>
						</Inline>
						<Box xcss={styles.padEmptyState} />
					</>
				)}
			</Stack>
		</Stack>
	);
};
